import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { get, isEmpty } from "lodash";
import configDetails from "../constants/config";

// Create a token provider function that can be set by the auth context
let authAccessor: () => Promise<any | null> = async () => null;

export const setTokenProvider = (fn: () => Promise<any | null>): void => {
  authAccessor = fn;
};

const redirectToLogin = async (): Promise<null | void> => {
  const user = await authAccessor();
  if (!user) {
    return null;
  }
  if (typeof user?.logout === "function") {
    user.logout().catch((_error: Error) => {
      /* noop */
    });
  }
};

// Helper function to get valid token
const getAccessToken = async (): Promise<string | null> => {
  const user = await authAccessor();

  if (!user) {
    const token = sessionStorage.getItem(
      `oidc.user:${configDetails.KEYCLOAK.apiHost}${configDetails.KEYCLOAK.apiPath}/realms/${configDetails.realm}/:${configDetails.realm}`
    );
    if (token) {
      const obj = JSON.parse(token);
      return obj.access_token;
    }
    return null;
  }

  // If within 30s of expiry, attempt silent renew
  const now = Math.floor(Date.now() / 1000);
  if (user.expires_at && user.expires_at - now < 30 && typeof user.renew === "function") {
    try {
      const renewed = await user.renew();
      return renewed?.access_token || null;
    } catch {
      return null;
    }
  }
  return user.access_token || null;
};

const fetcher = async (
  method: string,
  endpoint: string,
  data: unknown = null,
  authenticated: boolean = true,
  baseUrlPath: string = ""
): Promise<unknown> => {
  const url =
    baseUrlPath === ""
      ? configDetails.ADMIN.apiHost + configDetails.ADMIN.apiPath
      : (configDetails as any)[baseUrlPath].apiHost + (configDetails as any)[baseUrlPath].apiPath;

  const config: AxiosRequestConfig = { method: method as Method, url: `${url}${endpoint}`, responseType: "json" };

  if (authenticated) {
    const token = await getAccessToken();

    if (!token) {
      redirectToLogin();
      return Promise.reject(new Error("No valid authentication token"));
    }

    if (isEmpty(baseUrlPath)) {
      config.headers = {
        Authorization: `Bearer ${token}`,
        "x-companyId": localStorage.getItem("company-id") || "",
        "x-companyName": encodeURI(localStorage.getItem("company-name") || ""),
      };
      if (sessionStorage.getItem("x-jobId")) {
        config.headers["x-jobId"] = encodeURI(sessionStorage.getItem("x-jobId") || "");
      }
    } else {
      config.headers = { Authorization: `Bearer ${token}` };
    }

    if (method === "patch") {
      config.headers["Content-Type"] = "application/json-patch+json";
    }
  }

  if (data) {
    config.data = data;
  }

  return axios(config)
    .then((response: AxiosResponse) => {
      return method === "get" ? response : { response, getUrl: url };
    })
    .catch((errorResponse: AxiosError | Error | any) => {
      const errorDetail = errorResponse.response ?? errorResponse;
      const reloadCall = errorDetail?.config?.url?.split("/");
      const status = errorDetail?.status;
      const innerData = errorDetail?.innerData;
      const statusText = get(errorDetail, "statusText");
      let errorMessage = innerData?.message ?? statusText;

      if (errorMessage === undefined) {
        errorMessage = errorResponse.message;
      }

      if (status === 401) {
        window.dispatchEvent(new CustomEvent("unauthorized"));
        // Return a never-settling promise so the rejection doesn't bubble up
        // as an unhandled runtime error — the modal handles the UX response.
        return Promise.resolve({ response: errorMessage, isApiError: true, status });
      }

      const isAiEngine404 = baseUrlPath === "AIMATCHINGENGINE" && status === 404;
      const isIntegrationEndpoint =
        endpoint.includes("costbytier") ||
        endpoint.includes("/ConnectHRIntegration") ||
        endpoint.includes("/ConnectIntelliHr") ||
        endpoint.includes("/IntegrationDetails");

      if (status !== 400 && !isAiEngine404 && !isIntegrationEndpoint) {
        sessionStorage.setItem("errorItem", "true");
        sessionStorage.setItem("status", String(status));
        sessionStorage.setItem("errorMsg", errorMessage);
      }

      if (reloadCall?.length >= 7 && reloadCall[6] === "organisationsettings" && status === 400) {
        sessionStorage.setItem("reload", "true");
      }

      const error: Error & { reason?: any; hideMessage?: boolean; details?: any; status?: number } = new Error(
        errorMessage
      );
      error.status = status;
      if (!isAiEngine404) {
        return Promise.resolve({ response: errorMessage, isApiError: true, status });
      }

      return Promise.resolve({ response: errorMessage, isApiError: true, status });
    });
};

export default fetcher;
